import { useState, useEffect } from 'react';
import { useApi } from '@/hooks/useApi';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { DataTable, Column } from '@/components/common/DataTable';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { EmptyState } from '@/components/common/EmptyState';
import {
  DriverCodSettlement,
  DriverPaySettlement,
} from '@/types/financial.types';
import { formatCurrency, formatDate } from '@/lib/formatters';
import { Receipt, RefreshCw, Wallet, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '@/contexts/I18nContext';

interface DriverSettlementHistoryProps {
  driverId: string;
  showCommission?: boolean;
  commissionRate?: number;
}

export function DriverSettlementHistory({ driverId }: DriverSettlementHistoryProps) {
  const { t } = useI18n();
  const codApi = useApi();
  const payApi = useApi();
  const navigate = useNavigate();

  const [codSettlements, setCodSettlements] = useState<DriverCodSettlement[]>([]);
  const [paySettlements, setPaySettlements] = useState<DriverPaySettlement[]>([]);
  const [hasFetched, setHasFetched] = useState(false);
  const [openCod, setOpenCod] = useState<boolean>(true);
  const [openPay, setOpenPay] = useState<boolean>(true);

  const STATUS_LABELS: Record<string, { label: string; className: string }> = {
    draft: { label: t.financials.status_draft, className: 'text-gray-600 bg-gray-50' },
    confirmed: { label: t.financials.status_confirmed, className: 'text-blue-600 bg-blue-50' },
    paid: { label: t.financials.status_paid, className: 'text-green-600 bg-green-50' },
    reversed: { label: t.financials.status_reversed, className: 'text-red-600 bg-red-50' },
  };

  useEffect(() => {
    if (driverId) fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driverId]);

  const fetchAll = async () => {
    setHasFetched(false);
    try {
      const [codRes, payRes]: any[] = await Promise.all([
        codApi.get(`/api/v1/driver-cod-settlements?driver_id=${driverId}`),
        payApi.get(`/api/v1/driver-pay-settlements?driver_id=${driverId}`),
      ]);
      const codData = codRes?.success !== undefined ? codRes?.data : codRes;
      const payData = payRes?.success !== undefined ? payRes?.data : payRes;
      setCodSettlements(Array.isArray(codData) ? codData : codData?.list ?? codData?.data ?? []);
      setPaySettlements(Array.isArray(payData) ? payData : payData?.list ?? payData?.data ?? []);
    } catch {
      toast.error('فشل في تحميل التسويات');
      setCodSettlements([]);
      setPaySettlements([]);
    } finally {
      setHasFetched(true);
    }
  };

  const codColumns: Column<DriverCodSettlement>[] = [
    {
      header: 'تاريخ الإنشاء',
      accessor: (row) => <span className="text-sm">{formatDate(row.created_at)}</span>,
      className: 'min-w-[120px]',
    },
    {
      header: 'قيمة الشحنات',
      accessor: (row) => (
        <span className="font-medium text-green-600">{formatCurrency(row.shipment_value)}</span>
      ),
      className: 'min-w-[120px]',
    },
    {
      header: 'رسوم التوصيل',
      accessor: (row) => (
        <span className="font-medium text-blue-600">{formatCurrency(row.delivery_fee)}</span>
      ),
      className: 'min-w-[120px]',
    },
    {
      header: 'الإجمالي',
      accessor: (row) => (
        <span className="font-bold text-primary">{formatCurrency(row.total_amount)}</span>
      ),
      className: 'min-w-[120px]',
    },
    {
      header: 'الحالة',
      accessor: (row) => {
        const s = STATUS_LABELS[row.status] ?? STATUS_LABELS.draft;
        return <span className={cn('text-xs px-2 py-0.5 rounded', s.className)}>{s.label}</span>;
      },
      className: 'min-w-[100px]',
    },
  ];

  const payColumns: Column<DriverPaySettlement>[] = [
    {
      header: 'الفترة',
      accessor: (row) => (
        <span className="text-sm">
          {formatDate(row.period_start)} — {formatDate(row.period_end)}
        </span>
      ),
      className: 'min-w-[180px]',
    },
    {
      header: t.financials.trips_count,
      accessor: (row) => <span className="text-sm">{row.trips_count}</span>,
      className: 'min-w-[80px]',
    },
    {
      header: t.financials.base_amount,
      accessor: (row) => <span>{formatCurrency(row.base_amount)}</span>,
      className: 'min-w-[120px]',
    },
    {
      header: t.financials.bonus_amount,
      accessor: (row) => <span className="text-green-600">{formatCurrency(row.bonus_amount)}</span>,
      className: 'min-w-[120px]',
    },
    {
      header: t.financials.total_amount,
      accessor: (row) => (
        <span className="font-bold text-primary">{formatCurrency(row.total_amount)}</span>
      ),
      className: 'min-w-[120px]',
    },
    {
      header: 'الحالة',
      accessor: (row) => {
        const s = STATUS_LABELS[row.status] ?? STATUS_LABELS.draft;
        return <span className={cn('text-xs px-2 py-0.5 rounded', s.className)}>{s.label}</span>;
      },
      className: 'min-w-[100px]',
    },
  ];

  if (!hasFetched && codSettlements.length === 0 && paySettlements.length === 0) {
    return <LoadingSkeleton type="card" rows={3} />;
  }

  const loading = codApi.loading || payApi.loading;

  return (
    <div className="space-y-4" dir="rtl">
      <div className="flex justify-end">
        <Button
          variant="outline"
          size="icon"
          onClick={fetchAll}
          disabled={loading}
          className="h-8 w-8"
        >
          <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
        </Button>
      </div>

      {/* COD Settlements */}
      <Card>
        <CardHeader className="pb-3">
          <Button
            variant="ghost"
            className="w-full h-auto p-0 flex items-center justify-between hover:bg-transparent"
            onClick={() => setOpenCod(!openCod)}
          >
            <div className="flex items-center gap-2">
              <Receipt className="h-4 w-4" />
              <CardTitle className="text-base">{t.financials.cod_settlements}</CardTitle>
              <span className="text-xs text-muted-foreground">({codSettlements.length})</span>
            </div>
            {openCod ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </CardHeader>
        {openCod && (
          <CardContent>
            {codSettlements.length === 0 ? (
              <EmptyState
                icon={<Receipt className="h-10 w-10" />}
                title={t.financials.no_unsettled_cod}
              />
            ) : (
              <DataTable
                data={codSettlements}
                columns={codColumns}
                actions={(row) => (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => navigate(`/dashboard/driver-cod-settlements/${row.id}`)}
                  >
                    عرض
                  </Button>
                )}
                emptyMessage={t.financials.no_unsettled_cod}
              />
            )}
          </CardContent>
        )}
      </Card>

      {/* Pay Settlements */}
      <Card>
        <CardHeader className="pb-3">
          <Button
            variant="ghost"
            className="w-full h-auto p-0 flex items-center justify-between hover:bg-transparent"
            onClick={() => setOpenPay(!openPay)}
          >
            <div className="flex items-center gap-2">
              <Wallet className="h-4 w-4" />
              <CardTitle className="text-base">{t.financials.pay_settlements}</CardTitle>
              <span className="text-xs text-muted-foreground">({paySettlements.length})</span>
            </div>
            {openPay ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </CardHeader>
        {openPay && (
          <CardContent>
            {paySettlements.length === 0 ? (
              <EmptyState
                icon={<Wallet className="h-10 w-10" />}
                title={t.financials.no_unsettled_pay}
              />
            ) : (
              <DataTable
                data={paySettlements}
                columns={payColumns}
                actions={(row) => (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => navigate(`/dashboard/driver-pay-settlements/${row.id}`)}
                  >
                    عرض
                  </Button>
                )}
                emptyMessage={t.financials.no_unsettled_pay}
              />
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
}
